import React, { useState, useEffect, useContext } from 'react';
import querystring from 'query-string';
import env from 'consts/env';
import api from 'api';
import { Notify } from 'utils';
import { useAuth } from 'providers/AuthProvider/SessionAuthProvider';

export enum Action {
  IMPORT_CLIENT = 'IMPORT_CLIENT'
}

interface ContextAuthIo {
  isAuthenticated: boolean;
  isProcessing: boolean;
  signIn: ({
    redirectUrl,
    action
  }: {
    redirectUrl?: string;
    action?: Action;
  }) => void;
  clearAction: () => void;
}

export const AuthIOContext = React.createContext({});

// @ts-ignore
export const useAuthIo = (): ContextAuthIo => useContext(AuthIOContext);

interface Props {
  children: any;
}

const AuthIoProvider = ({ children }: Props) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessing, setProcessing] = useState(false);
  const { isAuthenticated: isAppAuthenticated } = useAuth();

  // Check if redirect from Inteliflo
  const { io: isRedirectedFromIo, code: ioVeryficationCode } =
    querystring.parse(window.location.search);

  const clearAction = () => localStorage.removeItem('action');

  useEffect(() => {
    // Check auth status at firts run
    if (!isRedirectedFromIo && isAppAuthenticated) {
      api.intelifloApi.checkAuthStatus().then(status => {
        setIsAuthenticated(status.data.is_authorized);
      });
    }
  }, [isRedirectedFromIo, isAppAuthenticated]);

  useEffect(() => {
    if (ioVeryficationCode && isRedirectedFromIo && isAppAuthenticated) {
      setProcessing(true);

      // CALL API TO VALIDATE USER TOKEN
      api.intelifloApi
        .verifyIoCode({ code: ioVeryficationCode as string })
        .then(() => {
          setIsAuthenticated(true);
          setProcessing(false);
        })
        .catch(() => {
          Notify.error('Unable to authorize Intelliflo');
          setProcessing(false);
        });
    }
  }, [ioVeryficationCode, isRedirectedFromIo, isAppAuthenticated]);

  const signIn = ({
    redirectUrl,
    action
  }: {
    redirectUrl?: string;
    action?: Action;
  }) => {
    const redirect = `${env.BASE_URL}/${redirectUrl || ''}`;
    if (action) {
      localStorage.setItem('action', action);
    }
    const url = `https://identity.intelliflo.com/core/connect/authorize?response_type=code&scope=openid myprofile profile client_data client_financial_data offline_access&client_id=${env.IO_CLIENT}&redirect_uri=${redirect}?io=true`;

    window.location.href = url;
  };

  return (
    <AuthIOContext.Provider
      value={{
        isAuthenticated,
        isProcessing,
        signIn,
        clearAction
      }}
    >
      {children}
    </AuthIOContext.Provider>
  );
};

export default AuthIoProvider;
